import { ArrowRight, CheckCircle2, Loader2, TriangleAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useT } from '@/components/visitone/visitone-provider'
import { ModuleShell } from '@/components/today/module-shell'
import { formatRelative } from '@/components/today/format'
import { useTodayModule } from '@/hooks/use-today-module'
import type { ActivityEntry } from '@/lib/bff/today'
import type { TodayModuleProps } from '@/components/today/modules/types'

function StatusIcon({ status }: { status: ActivityEntry['status'] }) {
  if (status === 'success') return <CheckCircle2 aria-hidden="true" className="size-4 text-success-text" />
  if (status === 'failed') return <TriangleAlert aria-hidden="true" className="size-4 text-danger" />
  return <Loader2 aria-hidden="true" className="size-4 text-fg-muted motion-safe:animate-spin" />
}

export function RecentActivityModule({
  tenantId,
  venue,
  headingId,
  onOpenRoute,
  canOpenRoute,
  onDismiss,
  refusal,
}: TodayModuleProps) {
  const t = useT()
  const title = t('today.moduleTitles.recent-activity')
  const { data, error, isLoading, retry } = useTodayModule('recent-activity', tenantId, venue)

  // Hidden entirely when there is no recent activity (VIS-257 AC5).
  if (!isLoading && !error && (!data || data.entries.length === 0)) return null

  const statusLabel = (status: ActivityEntry['status']) =>
    t(`today.activity.status${status.charAt(0).toUpperCase()}${status.slice(1)}`)

  return (
    <ModuleShell
      title={title}
      headingId={headingId}
      isLoading={isLoading}
      error={error}
      onRetry={retry}
      onDismiss={onDismiss}
      refusal={refusal}
      loadingRows={4}
    >
      <ul className="flex flex-col divide-y divide-line">
        {data?.entries.slice(0, 5).map((entry) => (
          <li key={entry.id} className="flex items-center justify-between gap-3 py-2.5 first:pt-0">
            <span className="flex min-w-0 items-center gap-2 text-sm text-fg">
              <StatusIcon status={entry.status} />
              <span className="min-w-0 wrap-anywhere">{entry.job}</span>
            </span>
            <span className="flex shrink-0 items-center gap-2 text-xs text-fg-muted">
              <span>{statusLabel(entry.status)}</span>
              <span aria-hidden="true">·</span>
              <time dateTime={entry.at}>{formatRelative(entry.at)}</time>
            </span>
          </li>
        ))}
      </ul>
      {canOpenRoute('/activity') ? (
        <div>
          <Button variant="secondary" size="sm" onClick={() => onOpenRoute('/activity')}>
            {t('today.activity.open')}
            <ArrowRight />
          </Button>
        </div>
      ) : null}
    </ModuleShell>
  )
}
